import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { LandingPage } from '@/pages/LandingPage'
import { HomePage } from '@/pages/HomePage'
import { ViewSharePage } from '@/pages/ViewSharePage'
import { EmbedPage } from '@/pages/EmbedPage'
import { RawPage } from '@/pages/RawPage'
import { ShareCreatedPage } from '@/pages/ShareCreatedPage'
import { ShareManagePage } from '@/pages/ShareManagePage'
import { SettingsPage } from '@/pages/SettingsPage'
import { DocsPage } from '@/pages/DocsPage'
import { ApiDocsPage } from '@/pages/ApiDocsPage'
import { TermsPage } from '@/pages/TermsPage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { PageContainer } from '@/components/layout/PageContainer'

// Full-width page wrapper (no padding, keeps footer)
function FullWidthPage({ children }: { children: React.ReactNode }) {
  return (
    <PageContainer fullWidth>
      {children}
    </PageContainer>
  )
}

// Editor page wrapper (no header, no footer)
function EditorPageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <PageContainer fullWidth hideHeader hideFooter>
      {children}
    </PageContainer>
  )
}

// Standard page wrapper (with padding and footer)
function StandardPage({ children }: { children: React.ReactNode }) {
  return (
    <PageContainer>
      {children}
    </PageContainer>
  )
}

// Landing page wrapper (edge-to-edge, no padding)
function LandingPageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <PageContainer noPadding>
      {children}
    </PageContainer>
  )
}

// Docs page wrapper
function DocsPageWrapper() {
  return (
    <PageContainer hideUserMenu>
      <DocsPage />
    </PageContainer>
  )
}

// API Docs page wrapper
function ApiDocsPageWrapper() {
  return (
    <PageContainer hideUserMenu>
      <ApiDocsPage />
    </PageContainer>
  )
}

function ScrollToTop() {
  const location = useLocation()
  useEffect(() => {
    // Don't scroll to top if there's a hash - let the browser handle anchor navigation
    if (!location.hash) {
      window.scrollTo(0, 0)
    }
  }, [location.pathname, location.hash])
  return null
}

export function AppRoutes() {
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<LandingPageWrapper><LandingPage /></LandingPageWrapper>} />
      <Route path="/editor" element={<EditorPageWrapper><HomePage /></EditorPageWrapper>} />
      <Route path="/view/:shareId" element={<FullWidthPage><ViewSharePage /></FullWidthPage>} />
      <Route path="/embed/:shareId" element={<EmbedPage />} />
      <Route path="/raw/:shareId" element={<RawPage />} />
      <Route path="/created/:shareId" element={<StandardPage><ShareCreatedPage /></StandardPage>} />
      <Route path="/edit/:token" element={<StandardPage><ShareManagePage /></StandardPage>} />
      {/* Login redirects to editor with auth modal */}
      <Route path="/login" element={<Navigate to="/editor?auth=signup" replace />} />
      <Route path="/settings" element={<StandardPage><SettingsPage /></StandardPage>} />
      <Route path="/docs" element={<DocsPageWrapper />} />
      <Route path="/docs/api" element={<ApiDocsPageWrapper />} />
      {/* /pricing redirects to landing page pricing section */}
      <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
      <Route path="/terms" element={<StandardPage><TermsPage /></StandardPage>} />
      <Route path="/privacy" element={<StandardPage><PrivacyPage /></StandardPage>} />
      <Route path="*" element={<StandardPage><NotFoundPage /></StandardPage>} />
    </Routes>
    </>
  )
}
